import { useCallback, useEffect, useRef, useState } from 'react';
import type { InspectionType } from '@room-verify/shared';
import {
  ApiError,
  api,
  loadToken,
  saveToken,
  type ActiveInspection,
  type CompletionResult,
  type Inspection,
  type LoginResult,
  type MyWork,
  type SessionUser,
} from './api';
import {
  IconArrowLeft,
  IconGlobe,
  IconLogout,
  IconShield,
  Sheet,
  ToastRegion,
  type ToastMessage,
} from './components';
import { LANGUAGES, dictionary, loadLanguage, saveLanguage, type Language } from './i18n';
import { CaptureScreen } from './screens/Capture';
import { HomeScreen } from './screens/Home';
import { LoginScreen } from './screens/Login';
import { RoomEntryScreen } from './screens/RoomEntry';
import { RoomItemsScreen } from './screens/RoomItems';
import { SuccessScreen } from './screens/Success';

type Screen =
  | { name: 'home' }
  | { name: 'room-entry'; inspectionType: InspectionType }
  | { name: 'capture'; inspection: Inspection }
  | { name: 'items'; inspection: Inspection }
  | { name: 'success'; result: CompletionResult };

/**
 * Remembers which inspection is open across a reload or a re-login. Only the id is
 * kept — the authoritative state (which slots are confirmed) always comes back from
 * the server, never from the device.
 */
const ACTIVE_KEY = 'roomverify.activeInspection';

const rememberActive = (id: string | null) => {
  try {
    if (id) localStorage.setItem(ACTIVE_KEY, id);
    else localStorage.removeItem(ACTIVE_KEY);
  } catch {
    /* the server is still the source of truth via /supervisor/active */
  }
};

export default function App() {
  const [language, setLanguage] = useState<Language>(loadLanguage);
  const copy = dictionary(language);

  const [token, setToken] = useState<string | null>(loadToken);
  const [user, setUser] = useState<SessionUser | null>(null);
  const [booting, setBooting] = useState(true);

  const [screen, setScreen] = useState<Screen>({ name: 'home' });
  const [work, setWork] = useState<MyWork | null>(null);
  const [workLoading, setWorkLoading] = useState(false);
  const [active, setActive] = useState<ActiveInspection | null>(null);

  const [reauthOpen, setReauthOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const toastId = useRef(0);

  const pushToast = useCallback(
    (text: string, tone: ToastMessage['tone'] = 'default') => {
      const id = (toastId.current += 1);
      setToasts((previous) => [...previous, { id, text, tone }]);
      setTimeout(() => setToasts((previous) => previous.filter((t) => t.id !== id)), 3200);
    },
    [],
  );

  const changeLanguage = useCallback((next: Language) => {
    setLanguage(next);
    saveLanguage(next);
    document.documentElement.lang = next;
  }, []);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  /* --------------------------------------------------------- session boot */

  useEffect(() => {
    let cancelled = false;

    const boot = async () => {
      const stored = loadToken();
      if (!stored) {
        setBooting(false);
        return;
      }
      try {
        const { user: me } = await api.me(stored);
        if (cancelled) return;
        if (me.role !== 'SUPERVISOR') {
          saveToken(null);
          setToken(null);
        } else {
          setUser(me);
          setToken(stored);
        }
      } catch {
        // A dead or expired token on a cold start is just "not signed in".
        if (!cancelled) {
          saveToken(null);
          setToken(null);
        }
      } finally {
        if (!cancelled) setBooting(false);
      }
    };

    void boot();
    return () => {
      cancelled = true;
    };
  }, []);

  /* ------------------------------------------------------------ data load */

  const refreshWork = useCallback(
    async (currentToken: string) => {
      setWorkLoading(true);
      try {
        setWork(await api.myWork(currentToken));
      } catch (error) {
        if (error instanceof ApiError && error.isAuthExpiry) setReauthOpen(true);
      } finally {
        setWorkLoading(false);
      }
    },
    [],
  );

  const refreshActive = useCallback(async (currentToken: string) => {
    try {
      const { inspection } = await api.activeInspection(currentToken);
      setActive(inspection);
      rememberActive(inspection?.id ?? null);
    } catch {
      /* an unavailable resume banner is not worth interrupting the supervisor for */
    }
  }, []);

  useEffect(() => {
    if (!token || !user) return;
    if (screen.name !== 'home') return;
    void refreshWork(token);
    void refreshActive(token);
  }, [token, user, screen.name, refreshWork, refreshActive]);

  /* ---------------------------------------------------------- auth events */

  const handleSignedIn = useCallback(
    (result: LoginResult) => {
      saveToken(result.token);
      setToken(result.token);
      setUser(result.user);
      setReauthOpen(false);
      void refreshWork(result.token);
      void refreshActive(result.token);
    },
    [refreshWork, refreshActive],
  );

  const signOut = useCallback(async () => {
    const current = token;
    saveToken(null);
    rememberActive(null);
    setToken(null);
    setUser(null);
    setWork(null);
    setActive(null);
    setScreen({ name: 'home' });
    if (current) {
      try {
        await api.logout(current);
      } catch {
        /* the local session is already gone; a failed server revoke is not fatal */
      }
    }
  }, [token]);

  /**
   * Session expiry never unmounts the flow.
   *
   * The current screen — including a captured photo waiting in a preview — stays
   * exactly where it is behind the sheet. After signing in again the supervisor
   * repeats the one action that failed, not the whole room.
   */
  const handleAuthExpired = useCallback(() => setReauthOpen(true), []);

  /* --------------------------------------------------------- flow actions */

  const startFlow = useCallback((inspectionType: InspectionType) => {
    setScreen({ name: 'room-entry', inspectionType });
  }, []);

  const onInspectionStarted = useCallback((inspection: Inspection) => {
    rememberActive(inspection.id);
    setScreen({ name: 'capture', inspection });
  }, []);

  const resumeActive = useCallback(async () => {
    if (!token || !active) return;
    try {
      const { inspection } = await api.getInspection(token, active.id);
      setScreen({ name: 'capture', inspection });
    } catch (error) {
      if (error instanceof ApiError && error.isAuthExpiry) setReauthOpen(true);
      else pushToast(copy.errUnknown, 'danger');
    }
  }, [token, active, copy, pushToast]);

  const onCompleted = useCallback(
    (result: CompletionResult) => {
      rememberActive(null);
      setActive(null);
      setScreen({ name: 'success', result });
      // My Work Today must reflect the room the moment it is finished.
      if (token) void refreshWork(token);
    },
    [token, refreshWork],
  );

  const goHome = useCallback(() => setScreen({ name: 'home' }), []);

  const canGoBack = screen.name === 'room-entry';

  /* ----------------------------------------------------------- rendering */

  if (booting) {
    return (
      <div className="boot">
        <span className="brand-mark brand-mark-lg" aria-hidden="true">
          <IconShield size={30} />
        </span>
        <span className="spinner boot-spinner" />
        <span className="sr-only">{copy.appName}</span>
      </div>
    );
  }

  if (!token || !user) {
    return (
      <LoginScreen
        copy={copy}
        language={language}
        onLanguageChange={changeLanguage}
        onSignedIn={handleSignedIn}
      />
    );
  }

  return (
    <div className="app app-frame">
      <header className="topbar">
        {canGoBack ? (
          <button className="icon-btn" onClick={goHome} aria-label={copy.back}>
            <IconArrowLeft size={20} />
          </button>
        ) : (
          <span className="brand-mark brand-mark-sm" aria-hidden="true">
            <IconShield size={18} />
          </span>
        )}

        <div className="topbar-title">
          <span className="topbar-name">{copy.appName}</span>
          <span className="topbar-user">{user.fullName}</span>
        </div>

        <div className="topbar-actions">
          <button
            className="icon-btn"
            onClick={() => setLangOpen(true)}
            aria-label={copy.language}
          >
            <IconGlobe size={19} />
          </button>
          <button className="icon-btn" onClick={signOut} aria-label={copy.signOut}>
            <IconLogout size={19} />
          </button>
        </div>
      </header>

      <main className="app-main">
        {screen.name === 'home' && (
          <HomeScreen
            copy={copy}
            work={work}
            loading={workLoading}
            active={active}
            onChooseType={startFlow}
            onResume={resumeActive}
            onDiscardActive={() => setActive(null)}
          />
        )}

        {screen.name === 'room-entry' && (
          <RoomEntryScreen
            copy={copy}
            token={token}
            inspectionType={screen.inspectionType}
            onStarted={onInspectionStarted}
            onAuthExpired={handleAuthExpired}
          />
        )}

        {screen.name === 'capture' && (
          <CaptureScreen
            copy={copy}
            token={token}
            inspection={screen.inspection}
            onAllCaptured={() => setScreen({ name: 'items', inspection: screen.inspection })}
            onAuthExpired={handleAuthExpired}
            onToast={pushToast}
          />
        )}

        {screen.name === 'items' && (
          <RoomItemsScreen
            copy={copy}
            token={token}
            inspection={screen.inspection}
            onCompleted={onCompleted}
            onAuthExpired={handleAuthExpired}
          />
        )}

        {screen.name === 'success' && (
          <SuccessScreen copy={copy} result={screen.result} onNext={goHome} />
        )}
      </main>

      {/* Re-login without losing the screen underneath. Not dismissible: closing it
          would leave the supervisor tapping into a session that cannot succeed. */}
      <Sheet
        open={reauthOpen}
        title={copy.errSessionExpired}
        onClose={() => setReauthOpen(false)}
        dismissible={false}
      >
        <LoginScreen
          copy={copy}
          language={language}
          onLanguageChange={changeLanguage}
          onSignedIn={handleSignedIn}
          compact
          note={`${copy.errSessionExpiredHelp} ${copy.sessionKeptSafe}`}
        />
      </Sheet>

      <Sheet open={langOpen} title={copy.language} onClose={() => setLangOpen(false)}>
        <div className="lang-list">
          {LANGUAGES.map((option) => (
            <button
              key={option.code}
              className={`lang-row-btn${option.code === language ? ' is-active' : ''}`}
              onClick={() => {
                changeLanguage(option.code);
                setLangOpen(false);
              }}
              aria-pressed={option.code === language}
            >
              {option.label}
            </button>
          ))}
        </div>
      </Sheet>

      <ToastRegion toasts={toasts} />
    </div>
  );
}
